import { isPlatformServer } from '@angular/common';
import {
  Directive,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { defaultImageLocation } from '../models/image-locations.model';

type ImageSrc = string | null | undefined;

@Directive({
  selector: '[dalDefaultImg]',
})
export class DefaultImgDirective implements OnChanges {
  @Input() src: ImageSrc = null;

  private defaultLocalImage = defaultImageLocation;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private imageRef: ElementRef,
    private renderer: Renderer2
  ) {}

  ngOnChanges(): void {
    this.initImage();
  }

  private initImage() {
    // Do not evaluate on SSR
    if (isPlatformServer(this.platformId)) {
      return;
    }

    // If `src` is empty or invalid, immediately use the default image
    if (!this.src) {
      this.setImage(this.defaultLocalImage);
      return;
    }

    // Show skeleton before image is loaded
    this.renderer.addClass(this.imageRef.nativeElement, 'g-skeleton');

    const img = new Image();

    // If possible to load image, set it to img
    img.onload = () => {
      this.setImage(this.resolveImage(this.src));
      this.renderer.removeClass(this.imageRef.nativeElement, 'g-skeleton');
    };

    img.onerror = () => {
      // Set a placeholder image if the image fails to load
      this.setImage(this.defaultLocalImage);
      this.renderer.removeClass(this.imageRef.nativeElement, 'g-skeleton');
    };

    // Triggers http request to load image
    img.src = this.resolveImage(this.src);
  }

  private setImage(src: ImageSrc) {
    this.imageRef.nativeElement.setAttribute('src', src);
  }

  private resolveImage(src: ImageSrc): string {
    // If src is empty or undefined, fall back to default
    return src ? src : this.defaultLocalImage;
  }
}
